import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeftRight, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { WarehouseScopeBar } from '../../components/WarehouseScopeBar';
import {
  stockTransfersOf, isWarehouseLocation, locationLabelOf, TRANSFER_STAGE_META,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(d);
  // eslint-disable-next-line no-unused-vars
  } catch (_e) {
    return dateString;
  }
};

const StatusBadge = ({ status }) => {
  const meta = TRANSFER_STAGE_META[status] || { label: status, color: '#64748b' };
  
  if (status === 'received') {
    return (
      <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: meta.color }}>
        {meta.label}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-[0.12em]"
      style={{ background: `${meta.color}1f`, color: meta.color }}
    >
      {meta.label}
    </span>
  );
};
StatusBadge.propTypes = { status: PropTypes.string.isRequired };

const totalQty = (t) => (t.lines || []).reduce((s, l) => s + (Number(l.qty) || 0), 0);
const itemsText = (t) => (t.lines || []).map((l) => l.name).filter(Boolean).join(', ');

// Read-only view of every stock transfer that touches a warehouse (in or out).
// Defaults to all warehouses; narrow with the picker. Org-admin only.
export const OrgWhTransportTab = ({ org }) => {
  const { version: dataVersion } = useSharedStore();
  const [scope, setScope] = useState('all');
  const [search, setSearch] = useState('');
  const accent = org.theme.primary;

  const rows = useMemo(() => {
    let list = stockTransfersOf(org.id).filter((t) => {
      if ((t.note || '').toLowerCase().includes('emergency')) return false;
      return scope === 'all'
        ? (isWarehouseLocation(t.fromLocationId) || isWarehouseLocation(t.toLocationId))
        : (t.fromLocationId === scope || t.toLocationId === scope);
    });
    list = [...list].sort((a, b) => {
      const ad = a.expectedDate || a.createdAt || '';
      const bd = b.expectedDate || b.createdAt || '';
      return ad < bd ? 1 : ad > bd ? -1 : 0;
    });
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((t) => {
      const text = `${t.id} ${t.carrier || ''} ${locationLabelOf(t.fromLocationId)} ${locationLabelOf(t.toLocationId)} ${itemsText(t)}`;
      return text.toLowerCase().includes(q);
    });
  }, [scope, search, org.id, dataVersion]);

  return (
    <div className="flex flex-col gap-4">
      <WarehouseScopeBar org={org} value={scope} onChange={setScope} accent={accent} />

      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-6 shadow-sm flex flex-col gap-4"
      >
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] inline-flex items-center gap-2">
              <ArrowLeftRight size={16} style={{ color: accent }} aria-hidden="true" />
              Transport details
            </h3>
            <p className="text-[11px] font-medium text-slate-500 mt-1">All stock transfers in or out of your warehouses (read-only).</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search route, carrier, item…"
                className="w-56 bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none"
              />
            </div>
          </div>
        </header>

        {rows.length === 0 ? (
          <div className="py-12 border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No transfers.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 border-b border-[var(--theme-tertiary)]">
                  <th className="py-2 pr-3 w-[15%]">From</th>
                  <th className="py-2 pr-3 w-[15%]">To</th>
                  <th className="py-2 pr-3 w-auto">Items</th>
                  <th className="py-2 px-3 w-16 text-right">Qty</th>
                  <th className="py-2 px-3 w-32 text-center">Carrier</th>
                  <th className="py-2 px-3 w-40">Date & Time</th>
                  <th className="py-2 pl-3 w-24">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-b border-[var(--theme-tertiary)] last:border-b-0">
                    <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{locationLabelOf(t.fromLocationId)}</td>
                    <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{locationLabelOf(t.toLocationId)}</td>
                    <td className="py-2 pr-3 text-slate-600 max-w-[220px] truncate" title={itemsText(t)}>{itemsText(t) || '—'}</td>
                    <td className="py-2 px-3 text-slate-700 text-right">{totalQty(t)}</td>
                    <td className="py-2 px-3 text-slate-600 text-center">{t.carrier || '—'}</td>
                    <td className="py-2 px-3 text-slate-600">
                      {(() => {
                        const dt = formatDateTime(t.expectedDate || t.createdAt);
                        if (dt.includes(', ')) {
                          const [dPart, tPart] = dt.split(', ');
                          return (
                            <div className="flex flex-col">
                              <span className="font-medium text-[#0d0d0d] whitespace-nowrap">{dPart}</span>
                              <span className="text-[10px] text-slate-400 mt-0.5 whitespace-nowrap">{tPart}</span>
                            </div>
                          );
                        }
                        return dt;
                      })()}
                    </td>
                    <td className="py-2 pl-3"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>
    </div>
  );
};

OrgWhTransportTab.propTypes = {
  org: PropTypes.object.isRequired,
};
